import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Paper,
  Fade,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Image as ImageIcon,
  Animation as AnimationIcon,
  Gradient as GradientIcon,
  Star as StarIcon,
  Nature as NatureIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Close as CloseIcon,
  Waves as WavesIcon,
  AcUnit as SnowIcon,
  Whatshot as FireIcon,
  GridOn as GridIcon,
  AutoAwesome as MagicIcon,
  ColorLens as RainbowIcon,
  Psychology as BrainIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeService } from '../../services/themeService';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';

export interface ThemeTemplate {
  id: string;
  name: string;
  type: 'gradient' | 'image' | 'animation';
  icon: React.ReactNode;
  preview: string;
  description: string;
}

const themeTemplates: ThemeTemplate[] = [
  {
    id: 'purple-blue-gradient',
    name: 'Purple Blue',
    type: 'gradient',
    icon: <GradientIcon />,
    preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Classic purple to blue gradient'
  },
  {
    id: 'sunset-gradient',
    name: 'Sunset',
    type: 'gradient',
    icon: <StarIcon />,
    preview: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
    description: 'Warm sunset colors'
  },
  {
    id: 'ocean-gradient',
    name: 'Ocean',
    type: 'gradient',
    icon: <NatureIcon />,
    preview: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
    description: 'Deep ocean blues'
  },
  {
    id: 'forest-gradient',
    name: 'Forest',
    type: 'gradient',
    icon: <NatureIcon />,
    preview: 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)',
    description: 'Natural forest greens'
  },
  {
    id: 'dark-gradient',
    name: 'Dark Mode',
    type: 'gradient',
    icon: <DarkModeIcon />,
    preview: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
    description: 'Dark and mysterious'
  },
  {
    id: 'light-gradient',
    name: 'Light Mode',
    type: 'gradient',
    icon: <LightModeIcon />,
    preview: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    description: 'Clean and bright'
  },
  {
    id: 'aurora-gradient',
    name: 'Aurora',
    type: 'animation',
    icon: <AnimationIcon />,
    preview: 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #f5576c)',
    description: 'Animated aurora effect'
  },
];

interface ThemeSwitcherProps {
  currentTheme: string;
  onThemeChange: (themeId: string) => void;
}

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ currentTheme, onThemeChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredTheme, setHoveredTheme] = useState<string | null>(null);
  const open = Boolean(anchorEl);
  const { settings } = useWebsiteSettings();

  const siteTemplate = React.useMemo<ThemeTemplate>(() => {
    const t = ThemeService.getThemeById('site-default');
    const platformName = String(settings?.branding?.platformName || settings?.platformName || settings?.name || '').trim();
    return {
      id: 'site-default',
      name: platformName || t?.name || 'Default',
      type: 'gradient',
      icon: <MagicIcon />,
      preview: t?.css || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      description: 'Matches your website colors',
    };
  }, [settings?.branding?.platformName, settings?.platformName, settings?.name, settings?.colorScheme]);

  const templates = React.useMemo(() => {
    return [siteTemplate, ...themeTemplates.filter(t => t.id !== 'site-default')];
  }, [siteTemplate]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeSelect = (themeId: string) => {
    onThemeChange(themeId);
    handleClose();
  };

  const currentTemplate = templates.find(t => t.id === currentTheme) || templates[0];

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title="Change Theme">
        <IconButton
          onClick={handleClick}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <PaletteIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
            minWidth: 300,
            maxHeight: 400,
            overflow: 'auto',
            '& .MuiMenuItem-root': {
              borderRadius: 2,
              margin: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
            Choose Theme
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Select a background theme for your dashboard
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        {templates.map((template) => (
          <MenuItem
            key={template.id}
            onClick={() => handleThemeSelect(template.id)}
            onMouseEnter={() => setHoveredTheme(template.id)}
            onMouseLeave={() => setHoveredTheme(null)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 1.5,
              px: 2,
              backgroundColor: currentTheme === template.id ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: template.preview,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                border: currentTheme === template.id ? '2px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease',
                transform: hoveredTheme === template.id ? 'scale(1.1)' : 'scale(1)',
              }}
            >
              {template.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                {template.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {template.description}
              </Typography>
            </Box>
            {currentTheme === template.id && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#667eea',
                  }}
                >
                  ✓
                </Box>
              </motion.div>
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ThemeSwitcher;
